/**
 * OTP-related handlers extracted from `useForm/index.tsx`.
 *
 * Owns the SMS/email OTP submit, resend, and edit-phone flows that all
 * route through `sendDataToServer` and dispatch operation/state updates
 * on the parent reducer.
 *
 * Public API (returned object) matches the previous in-file shape so the
 * parent hook can spread it into its return value untouched.
 */

import { ActionTypes, FormReducerAction, InitState } from "../types";
import type { SubmitLeadFormResponse } from "src/api/submitLead";

interface UseOtpParams {
    state: InitState;
    dispatch: (action: FormReducerAction) => void;
    sendDataToServer: (args: {
        submit: boolean;
        formOtp?: string;
        buyerIds?: (number | string)[];
    }) => Promise<SubmitLeadFormResponse>;
    setLoading: (loading: boolean) => void;
}

export const useOtp = ({
    state,
    dispatch,
    sendDataToServer,
    setLoading,
}: UseOtpParams) => {
    const { operation } = state;

    const selectedBuyerIds = (): (number | string)[] | undefined =>
        operation?.firstRoundBuyers
            ?.filter((i) => i.selected)
            ?.map((buyer) => buyer.buyerId);

    const handleOtpSubmit = async (
        formOtp: string,
    ): Promise<{ success: boolean }> => {
        setLoading(true);

        const { success, data } = await sendDataToServer({
            submit: true,
            formOtp,
            buyerIds: selectedBuyerIds(),
        });

        setLoading(false);

        if (!success) {
            return { success: false };
        }

        if (data?.operationId) {
            dispatch({
                type: ActionTypes.SET_OPERATION,
                payload: {
                    operationId: data.operationId,
                    key: "ROUND_ONE",
                    isOtpResolved: true,
                },
            });
        }
        return { success: true };
    };

    const handleOtpResend = async (): Promise<{ success: boolean }> => {
        const { success } = await sendDataToServer({
            submit: true,
            buyerIds: selectedBuyerIds(),
        });
        return { success };
    };

    const handleOtpEditPhoneNumber = async () => {
        setLoading(true);
        const { success } = await sendDataToServer({ submit: true });
        setLoading(false);
        return { success };
    };

    return {
        handleOtpSubmit,
        handleOtpResend,
        handleOtpEditPhoneNumber,
    };
};
